import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import Brick from './BrickCore';
import { Utils } from '../../services';
import WorkflowMessagePanel from '../../components/WorkflowMessagePanel';
import { resolveMessageShowHtml } from '../../services/workflowMessageShowUtils';
import { TranslatableField, TranslatableWysiwygField } from '../../components';
import { Field } from 'redux-form';
import { Button, Col, FormGroup, Label, FormText } from 'reactstrap';

const MessageShowRunner = ({ brick, context, onDone, t, i18n }) => {
    const autoDoneRef = useRef(false);
    const lang = Utils.i18nUiLang(i18n);
    const showContinue = String(brick.getOption('show_continue', 'true')).toLowerCase() !== 'false';
    const continueLabelOption = brick.getOption('continue_label');

    const resolvedHtml = useMemo(
        () => resolveMessageShowHtml(brick, context, lang),
        [brick, context, lang]
    );

    const continueLabel = useMemo(() => {
        if (typeof continueLabelOption === 'string' && continueLabelOption.trim())
            return continueLabelOption;
        return Utils.localized_or_fallback(
            continueLabelOption,
            lang,
            t('workflows.bricks.message.show.continue')
        );
    }, [continueLabelOption, lang, t]);

    useEffect(() => {
        if (showContinue || autoDoneRef.current)
            return;
        autoDoneRef.current = true;
        onDone(context, () => {});
    }, [showContinue, context, onDone]);

    const handleContinue = () => {
        onDone(context, () => {});
    };

    const actions = showContinue ? (
        <Button color="success" onClick={handleContinue}>
            {continueLabel}
        </Button>
    ) : null;

    if (!resolvedHtml && !actions)
        return null;

    return (
        <div className="MessageShowBrick">
            <WorkflowMessagePanel mainHtml={resolvedHtml} actions={actions} />
        </div>
    );
};

/** Workflow brick: display a contextual message to the user. */
class MessageShowBrick extends Brick {
    getOptionsForm(props) {
        const { t } = props;

        return (
            <>
                <FormGroup row>
                    <Label sm={12}>{t('workflows.bricks.message.show.options.message')}</Label>
                    <Col sm={12}>
                        <Field
                            name={`bricks[${this.position}].options.message`}
                            component={TranslatableWysiwygField}
                        />
                        <FormText color="muted">
                            {t('workflows.bricks.message.show.options.message_help')}
                        </FormText>
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label sm={12}>{t('workflows.bricks.message.show.options.continue_label')}</Label>
                    <Col sm={12}>
                        <TranslatableField
                            name={`bricks[${this.position}].options.continue_label`}
                            component="input"
                            placeholder={t('workflows.bricks.message.show.options.continue_label_placeholder')}
                            optional
                        />
                    </Col>
                </FormGroup>
                <FormGroup row>
                    <Label sm={12}>{t('workflows.bricks.message.show.options.show_continue')}</Label>
                    <Col sm={12}>
                        <Field component="select" name={`bricks[${this.position}].options.show_continue`} className="form-control">
                            <option value="true">{t('common.yes')}</option>
                            <option value="false">{t('common.no')}</option>
                        </Field>
                        <FormText color="muted">
                            {t('workflows.bricks.message.show.options.show_continue_help')}
                        </FormText>
                    </Col>
                </FormGroup>
            </>
        );
    }

    run(context, _isEmbedded, onDone) {
        const brick = this;
        const Runner = () => {
            const { t, i18n } = useTranslation();
            return <MessageShowRunner brick={brick} context={context} onDone={onDone} t={t} i18n={i18n} />;
        };
        return <Runner />;
    }
}

export default MessageShowBrick;
